import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Briefcase,
  Target,
  MegaphoneSimple,
  BookOpen,
  PencilLine,
  ChartLineUp,
  EnvelopeSimple,
  Buildings,
  Lightbulb,
  CheckCircle,
  Circle,
  Sparkle,
} from '@phosphor-icons/react';

/**
 * Canonical PayGBot build sequence — MUST match the dashboard bot cards
 * exactly, because that's how users mentally model the flow:
 *
 *   Business Builder Bot → plan (seed, not in loop)
 *   Money Bot       → Offer → Sales Page → Lead Magnet → Emails → Essentials → Ebook
 *   Content Bot     → Find Viral Ideas → Generate Content
 *   Growth Bot      → Plan My Week (→ Run My Week, closes the loop)
 *
 * Viral Ideas ALWAYS comes before Create Content — that way the content a
 * user generates is seeded with a proven hook, not typed from scratch.
 *
 * After Plan My Week the user is in the "run the business in minutes a day"
 * loop. When the sequence ends we loop back to `viral_ideas` so the weekly
 * rhythm starts by sourcing a fresh idea.
 *
 * Keys must match the `currentStep` prop passed in by each builder modal.
 */
const STEPS = [
  { key: 'offer',      label: 'Build Offer',           icon: Briefcase,       color: 'from-blue-500 to-indigo-500' },
  { key: 'sales_page', label: 'Create Sales Page',     icon: MegaphoneSimple, color: 'from-pink-500 to-rose-500' },
  { key: 'lead_magnet',label: 'Create Lead Magnet',    icon: Target,          color: 'from-amber-500 to-orange-500' },
  { key: 'email',      label: 'Create Email Sequence', icon: EnvelopeSimple,  color: 'from-sky-500 to-blue-500' },
  { key: 'essentials', label: 'Business Essentials',   icon: Buildings,       color: 'from-emerald-500 to-teal-500' },
  { key: 'ebook',      label: 'Create Ebook',          icon: BookOpen,        color: 'from-purple-500 to-pink-500' },
  { key: 'viral_ideas',label: 'Find Viral Ideas',      icon: Lightbulb,       color: 'from-yellow-500 to-orange-500' },
  { key: 'content',    label: 'Generate Content',      icon: PencilLine,      color: 'from-violet-500 to-fuchsia-500' },
  { key: 'growth',     label: 'Plan My Week',          icon: ChartLineUp,     color: 'from-emerald-500 to-green-600' },
];

// After Plan My Week, users run their business weekly with this loop entry —
// Viral Ideas → Content → Growth → Viral Ideas → ...
const WEEKLY_LOOP_ENTRY = 'viral_ideas';

/**
 * Build a completed-step set from the user's creations + completed flags.
 * Kept lenient: any reasonable signal that a step is done counts.
 */
function deriveCompleted(progress = {}) {
  const s = new Set();
  if (progress.essentials_complete || progress.has_business_essentials || progress.has_business_memory) s.add('essentials');
  if (progress.offer_complete || progress.has_offer) s.add('offer');
  if (progress.lead_magnet_complete || progress.has_lead_magnet) s.add('lead_magnet');
  if (progress.sales_page_complete || progress.has_sales_page) s.add('sales_page');
  if (progress.ebook_complete || progress.has_ebook) s.add('ebook');
  if (progress.email_complete || progress.has_email_campaign) s.add('email');
  if (progress.content_complete || progress.has_content) s.add('content');
  if (progress.growth_complete || progress.has_growth) s.add('growth');
  return s;
}

/**
 * NextStepGuide
 * -------------
 * Replaces the generic "Done" button on every Money Bot success screen.
 * Shows:
 *   1. A compact progress tracker (current step marked complete)
 *   2. A large "Next Recommended Step" CTA
 *   3. A "Choose What to Build Next" grid of all tools (manual override)
 *
 * Props:
 *   currentStep  — key of the just-completed step (e.g., 'essentials')
 *   progress     — optional map of completion flags
 *   onPick(key)  — called when user picks a tool; the parent should close the
 *                  current modal and open the requested builder
 *   onDone       — called when the user clicks "I'm done for now" (exits the loop)
 */
export default function NextStepGuide({ currentStep, progress = {}, onPick, onDone }) {
  // Treat the just-completed step as complete, always
  const completed = deriveCompleted({ ...progress, [`${currentStep}_complete`]: true });

  // Recommended next step = the step immediately AFTER currentStep in the
  // canonical sequence. This mirrors how the dashboard reads top-to-bottom
  // and guarantees the user never bounces backwards (e.g., finishing Email
  // must never send them back to Build Offer).
  //
  // After the last step (`growth` / Plan My Week), we close the loop by
  // recommending the weekly run entry point so the "run in minutes a day"
  // flow keeps going.
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);
  let recommended;
  if (currentIdx >= 0 && currentIdx < STEPS.length - 1) {
    recommended = STEPS[currentIdx + 1];
  } else if (currentIdx === STEPS.length - 1) {
    // Just finished Plan My Week → enter the weekly loop (Content → Growth)
    recommended = STEPS.find((s) => s.key === WEEKLY_LOOP_ENTRY) || STEPS[0];
  } else {
    // Unknown currentStep — fall back to the first unfinished step so the
    // user is never stuck.
    recommended = STEPS.find((s) => !completed.has(s.key)) || STEPS[0];
  }

  return (
    <div className="space-y-5" data-testid="next-step-guide">
      {/* Progress tracker */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-3.5" data-testid="next-step-progress">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2.5">
          Your Business Build Progress
        </p>
        <div className="flex flex-wrap gap-x-3 gap-y-2">
          {STEPS.map((s) => {
            const isDone = completed.has(s.key);
            const isCurrent = s.key === currentStep;
            return (
              <div
                key={s.key}
                className="flex items-center gap-1.5 text-[11.5px]"
                data-testid={`progress-step-${s.key}`}
              >
                {isDone ? (
                  <CheckCircle size={14} weight="fill" className="text-emerald-400" />
                ) : (
                  <Circle size={14} className="text-slate-500" />
                )}
                <span className={isDone ? 'text-emerald-300 font-medium' : (isCurrent ? 'text-white' : 'text-slate-400')}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next Recommended Step */}
      {recommended && (
        <motion.button
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          onClick={() => onPick?.(recommended.key)}
          className={`w-full text-left bg-gradient-to-br ${recommended.color} hover:brightness-110 rounded-2xl p-4 transition-all shadow-lg`}
          data-testid={`next-step-recommended-${recommended.key}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-white/90 mb-1">
                <Sparkle size={11} weight="fill" />
                Next Recommended Step
              </div>
              <div className="text-white font-bold text-[17px] leading-tight">
                {recommended.label}
              </div>
              <div className="text-white/80 text-xs mt-1">
                Keep building — momentum matters. We&apos;ll carry your business memory over.
              </div>
            </div>
            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <ArrowRight size={18} weight="bold" className="text-white" />
            </div>
          </div>
        </motion.button>
      )}

      {/* Manual tool picker */}
      <div>
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">
          Or Choose What to Build Next
        </p>
        <div className="grid grid-cols-2 gap-2" data-testid="next-step-picker">
          {STEPS.filter((s) => s.key !== currentStep && (!recommended || s.key !== recommended.key)).map((s) => {
            const isDone = completed.has(s.key);
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => onPick?.(s.key)}
                className={`text-left bg-white/5 hover:bg-white/10 border rounded-xl p-3 transition-colors ${isDone ? 'border-emerald-500/30' : 'border-white/10'}`}
                data-testid={`pick-step-${s.key}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} className={isDone ? 'text-emerald-400' : 'text-slate-300'} weight="duotone" />
                  {isDone && <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-400">Done</span>}
                </div>
                <div className="text-white text-[13px] font-medium leading-tight">{s.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Soft exit — still available, just not the default action */}
      {onDone && (
        <button
          onClick={onDone}
          className="w-full text-slate-500 hover:text-slate-300 text-xs py-2"
          data-testid="next-step-exit-btn"
        >
          I&apos;m done for now
        </button>
      )}
    </div>
  );
}

export { STEPS as MONEY_BOT_STEPS };
